import React, { useState } from 'react';

interface SankeyLink {
  source: string;
  target: string;
  value: number;
}

interface Props {
  links: SankeyLink[];
}

// Distinct hues for expense categories (income is always green)
const EXPENSE_COLORS = ['#60a5fa', '#f472b6', '#fbbf24', '#a78bfa', '#fb923c', '#22d3ee', '#a3e635'];
const INCOME_COLOR = '#34d399';

const formatMoney = (v: number) =>
  `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Mobile-friendly alternative to the cash-flow Sankey. The backend builds a
 * 3-level flow (income sources → hub → expense categories → subcategories);
 * this reads it top-to-bottom as "Money In" / "Money Out" horizontal bars,
 * with expandable subcategories. Works on any flow payload (no d3-sankey, so
 * it is also immune to the circular-link crash).
 */
const CashFlowBreakdown: React.FC<Props> = ({ links }) => {
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});

  if (!links.length) {
    return <p className="chart-empty">No cash flow data available.</p>;
  }

  // The hub ("Income" / "Total Spending") is the node that fans out to the most
  // targets; tie-break by total outgoing value.
  const outDeg: Record<string, { count: number; sum: number }> = {};
  for (const l of links) {
    const e = (outDeg[l.source] ||= { count: 0, sum: 0 });
    e.count += 1;
    e.sum += l.value;
  }
  const hub = Object.entries(outDeg).sort(
    (a, b) => b[1].count - a[1].count || b[1].sum - a[1].sum,
  )[0][0];

  const inflows = links.filter(l => l.target === hub).sort((a, b) => b.value - a.value);
  const outflows = links.filter(l => l.source === hub).sort((a, b) => b.value - a.value);
  const subsOf = (cat: string) =>
    links.filter(l => l.source === cat).sort((a, b) => b.value - a.value);

  const totalIn = inflows.reduce((s, l) => s + l.value, 0);
  const totalOut = outflows.reduce((s, l) => s + l.value, 0);
  const maxIn = Math.max(...inflows.map(l => l.value), 1);
  const maxOut = Math.max(...outflows.map(l => l.value), 1);
  const net = totalIn - totalOut;

  const toggle = (cat: string) => setOpenCats(p => ({ ...p, [cat]: !p[cat] }));

  return (
    <div className="cf-breakdown">
      {inflows.length > 0 && (
        <>
          <div className="cf-section">
            <span className="cf-section-label">Money In</span>
            <span className="cf-section-total cf-in">{formatMoney(totalIn)}</span>
          </div>
          {inflows.map(l => (
            <div className="cf-row" key={`in-${l.source}`}>
              <div className="cf-row-head">
                <span className="cf-row-name">
                  <span className="cf-row-name--indent">{l.source}</span>
                </span>
                <span className="cf-row-amount">{formatMoney(l.value)}</span>
              </div>
              <div className="cf-bar">
                <div className="cf-bar-fill" style={{ width: `${(l.value / maxIn) * 100}%`, background: INCOME_COLOR }} />
              </div>
            </div>
          ))}
        </>
      )}

      <div className="cf-section">
        <span className="cf-section-label">Money Out</span>
        <span className="cf-section-total cf-out">{formatMoney(totalOut)}</span>
      </div>
      {outflows.map((l, i) => {
        const color = EXPENSE_COLORS[i % EXPENSE_COLORS.length];
        const subs = subsOf(l.target);
        const hasSubs = subs.length > 0;
        const open = !!openCats[l.target];
        const pct = totalOut > 0 ? Math.round((l.value / totalOut) * 100) : 0;
        const maxSub = Math.max(...subs.map(s => s.value), 1);

        const head = (
          <>
            <span className="cf-row-name">
              {hasSubs && <span className={`cf-chevron${open ? ' cf-chevron--open' : ''}`} aria-hidden="true">›</span>}
              <span className={hasSubs ? '' : 'cf-row-name--indent'}>{l.target}</span>
            </span>
            <span className="cf-row-vals">
              <span className="cf-row-amount">{formatMoney(l.value)}</span>
              <span className="cf-row-pct">{pct}%</span>
            </span>
          </>
        );

        return (
          <div className="cf-row" key={`out-${l.target}`}>
            {hasSubs ? (
              <button type="button" className="cf-row-head cf-row-head--btn" onClick={() => toggle(l.target)} aria-expanded={open}>
                {head}
              </button>
            ) : (
              <div className="cf-row-head">{head}</div>
            )}
            <div className="cf-bar">
              <div className="cf-bar-fill" style={{ width: `${(l.value / maxOut) * 100}%`, background: color }} />
            </div>
            {hasSubs && open && (
              <div className="cf-subs">
                {subs.map(s => (
                  <div className="cf-sub-row" key={`sub-${l.target}-${s.target}`}>
                    <div className="cf-row-head">
                      <span className="cf-sub-name">{s.target}</span>
                      <span className="cf-sub-amount">{formatMoney(s.value)}</span>
                    </div>
                    <div className="cf-bar cf-bar--sub">
                      <div className="cf-bar-fill" style={{ width: `${(s.value / maxSub) * 100}%`, background: color, opacity: 0.55 }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {inflows.length > 0 && (
        <div className="cf-net">
          <span className="cf-net-label">Net</span>
          <span className={`cf-net-value ${net >= 0 ? 'cf-in' : 'cf-out'}`}>
            {net >= 0 ? '+' : '-'}{formatMoney(Math.abs(net))}
          </span>
        </div>
      )}
    </div>
  );
};

export default CashFlowBreakdown;
